import { Box, Button, Card, FormLayout, InlineStack, RadioButton, Text, TextField, BlockStack } from '@shopify/polaris';
import { getScanModeStatus } from '../utils/helpers';
import { usePurchaseOrderEntry } from '../utils/hooks';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';

export default function PurchaseOrderEntrySection(props) {
    const { entryMode, finishedScanning, qtyValue, receivingLineItemsActive, receivingCartonsActive, receivingMode, setEntryMode, setQtyValue, displayStatus } = props;
    const {
        manualEntryLoading,
        submitFinishScanningLoading,
        skuValue,
        setSkuValue,
        barcodeValue,
        setBarcodeValue,
        handleFinishScanning,
        handleManualSubmit,
        fieldErrors,
        setFieldErrors,
        skuInputRef,
        lpnInputRef,
    } = usePurchaseOrderEntry(props);
    const scanModeStatus = getScanModeStatus(receivingMode, receivingCartonsActive, receivingLineItemsActive);

    return (
        <>
            {displayStatus !== 'Completed' && (
                <Card>
                    <Text variant="headingLg">
                        {receivingMode === 'receiving-line-items'
                            ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.LINE_ITEM_ENTRY
                            : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.CARTON_ENTRY}
                    </Text>
                    <Box style={{ paddingTop: '1rem' }}>
                        <InlineStack gap="200">
                            <RadioButton
                                label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.SCAN_MODE}
                                id="scan-mode"
                                name="entry-mode"
                                checked={entryMode === 'scan-mode'}
                                onChange={() => {
                                    setFieldErrors({});
                                    setEntryMode('scan-mode');
                                }}
                            />
                            <RadioButton
                                label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.MANUAL_MODE}
                                id="manual-entry"
                                name="entry-mode"
                                checked={entryMode === 'manual-entry'}
                                onChange={() => {
                                    setFieldErrors({});
                                    setEntryMode('manual-entry');
                                }}
                            />
                        </InlineStack>
                        {entryMode === 'scan-mode' ? (
                            <>
                                <BlockStack>
                                    <div style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
                                        <Text variant="bodyLg" as="p" alignment="center">
                                            {scanModeStatus}
                                        </Text>
                                    </div>
                                </BlockStack>
                                <InlineStack align="end" gap={200}>
                                    <Button variant="primary" onClick={handleFinishScanning} loading={submitFinishScanningLoading}>
                                        {finishedScanning
                                            ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.FINISHED_SCANNING
                                            : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.FINISH_SCANNING}
                                    </Button>
                                </InlineStack>
                            </>
                        ) : (
                            <FormLayout>
                                {receivingCartonsActive && (
                                    <FormLayout.Group>
                                        {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                        <div
                                            ref={lpnInputRef}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    handleManualSubmit();
                                                }
                                            }}
                                        >
                                            <TextField
                                                label={
                                                    receivingCartonsActive
                                                        ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.LICENSE_PLATE_NUMBER
                                                        : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.PRODUCT_BARCODE
                                                }
                                                value={barcodeValue}
                                                onChange={setBarcodeValue}
                                                autoComplete="off"
                                                onEnterPressed={handleManualSubmit}
                                                error={fieldErrors?.barcode}
                                            />
                                        </div>
                                    </FormLayout.Group>
                                )}

                                {!receivingCartonsActive && (
                                    <>
                                        <Box style={{ paddingTop: '.5rem' }}>
                                            <FormLayout.Group>
                                                {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                                <div
                                                    ref={skuInputRef}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            handleManualSubmit();
                                                        }
                                                    }}
                                                >
                                                    <TextField
                                                        label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.PRODUCT_SKU}
                                                        value={skuValue}
                                                        onChange={setSkuValue}
                                                        autoComplete="off"
                                                        error={fieldErrors?.sku}
                                                    />
                                                </div>

                                                {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                                <div
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            handleManualSubmit();
                                                        }
                                                    }}
                                                >
                                                    <TextField
                                                        label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.PRODUCT_BARCODE}
                                                        value={barcodeValue}
                                                        onChange={setBarcodeValue}
                                                        autoComplete="off"
                                                        onEnterPressed={handleManualSubmit}
                                                        error={fieldErrors?.barcode}
                                                    />
                                                </div>
                                            </FormLayout.Group>
                                        </Box>
                                        <FormLayout.Group>
                                            <Box style={{ paddingTop: '.5rem' }}>
                                                {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                                                <div
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            handleManualSubmit();
                                                        }
                                                    }}
                                                >
                                                    <TextField label={PURCHASE_ORDER_STRINGS.QUANTITY} value={qtyValue} onChange={setQtyValue} autoComplete="off" error={fieldErrors?.quantity} />
                                                </div>
                                            </Box>
                                        </FormLayout.Group>
                                    </>
                                )}
                                <InlineStack align="end">
                                    <Button variant="primary" onClick={handleManualSubmit} loading={manualEntryLoading} disabled={qtyValue.trim() === '' || isNaN(Number(qtyValue))}>
                                        {PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.SUBMIT}
                                    </Button>
                                </InlineStack>
                            </FormLayout>
                        )}
                    </Box>
                </Card>
            )}
        </>
    );
}
